/**
 * Locale definitions — phases.md §1.15.
 *
 * Assamese is the default locale, not English: this is a regional publication whose
 * primary audience reads Assamese. Both locales are URL-prefixed so a locale segment
 * can never be confused with a category slug.
 */
export const LOCALES = ['as', 'en'];
export const DEFAULT_LOCALE = 'as';
/** BCP-47 tags, for `<html lang>`, `Intl`, and `hreflang`. */
export const LOCALE_TAGS = {
    as: 'as-IN',
    en: 'en-IN',
};
/**
 * The tag used for **`Intl` formatting**, which is deliberately not `LOCALE_TAGS`.
 *
 * `<html lang>` and `hreflang` must stay true language tags — an Assamese page is `as-IN`
 * and nothing else. Formatting cannot use that tag, and the reason is measurable:
 *
 *   Node 20          `as-IN` → ২,০২৬ · ২১ মে’ ২০২৬   (full ICU, real Assamese data)
 *   Chrome 153       `as-IN` → 2,026 · 21 May 2026   (no `as` data at all —
 *                    `supportedLocalesOf(['as-IN'])` is empty, so it falls back to en-GB
 *                    **and discards the `-u-nu-beng` extension with it**)
 *
 * The same component would then render Bengali numerals on the server and Latin ones in
 * the browser: a hydration mismatch on every date and reading time, and a front page that
 * contradicts the supplied Assamese design, which uses Bengali numerals throughout.
 *
 * `bn-IN-u-nu-beng` is supported by both engines and produces byte-identical output in
 * each (verified for numbers, dates and times). Bengali is the same script with the same
 * numerals and the same Indian grouping; the cost is Bengali month abbreviations rather
 * than Assamese ones — "২১ মে, ২০২৬" instead of "২১ মে’ ২০২৬". A dictionary of Assamese
 * month names would remove even that, and is noted for Phase 17.
 */
export const LOCALE_INTL_TAGS = {
    as: 'bn-IN-u-nu-beng',
    en: 'en-IN',
};
/** Native names, for the language switcher — never translate these. */
export const LOCALE_NAMES = {
    as: 'অসমীয়া',
    en: 'English',
};
export function isLocale(value) {
    return typeof value === 'string' && LOCALES.includes(value);
}
/**
 * Picks a locale from an `Accept-Language` header. Deliberately simple: exact tag or
 * primary subtag, highest q-value wins, default otherwise. No fuzzy matching — a reader
 * who wants the other language uses the switcher, and the choice is remembered in a cookie.
 */
export function negotiateLocale(acceptLanguage) {
    if (!acceptLanguage)
        return DEFAULT_LOCALE;
    const ranked = acceptLanguage
        .split(',')
        .map((part) => {
        const [tag, ...params] = part.trim().split(';');
        const q = params.find((p) => p.trim().startsWith('q='));
        const quality = q ? Number.parseFloat(q.split('=')[1] ?? '1') : 1;
        return {
            tag: (tag ?? '').trim().toLowerCase(),
            quality: Number.isNaN(quality) ? 0 : quality,
        };
    })
        .filter((entry) => entry.tag.length > 0)
        .sort((a, b) => b.quality - a.quality);
    for (const { tag } of ranked) {
        const primary = tag.split('-')[0];
        if (isLocale(primary))
            return primary;
    }
    return DEFAULT_LOCALE;
}
/** Builds a locale-prefixed path: `localePath('as', 'sports', 'my-slug')` → `/as/sports/my-slug`. */
export function localePath(locale, ...segments) {
    const parts = segments.filter((s) => s.length > 0).map((s) => s.replace(/^\/+|\/+$/g, ''));
    return `/${[locale, ...parts].join('/')}`;
}
//# sourceMappingURL=locales.js.map